import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { Field } from './ui.jsx';
import { useConfirm, useToast } from './feedback.jsx';
import { t } from '../i18n/index.jsx';

/**
 * Enroll / disable TOTP two-factor authentication from the Account page.
 * Enrollment flow: setup() gets a pending secret + otpauth URI (rendered as
 * a QR code entirely client-side — the secret never leaves the browser
 * except to our own API), confirm() with the first 6-digit code turns it on
 * and returns one-time recovery codes shown exactly once.
 */
export default function TwoFactorSetup() {
  const toast = useToast();
  const confirm = useConfirm();
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [secret, setSecret] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function loadStatus() {
    const r = await api.get('/auth/2fa/status');
    setEnabled(r.enabled);
  }
  useEffect(() => { loadStatus(); }, []);

  async function startEnroll() {
    setError(''); setBusy(true);
    try {
      const r = await api.post('/auth/2fa/setup');
      setSecret(r.secret);
      const url = await QRCode.toDataURL(r.otpauth_uri, { width: 220, margin: 1 });
      setQrDataUrl(url);
      setEnrolling(true);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function confirmEnroll() {
    setError(''); setBusy(true);
    try {
      const r = await api.post('/auth/2fa/confirm', { code: code.trim() });
      setRecoveryCodes(r.recovery_codes);
      setEnrolling(false);
      setCode('');
      setEnabled(true);
      toast(t('Two-factor authentication enabled'));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function disable() {
    if (!password) return;
    if (!(await confirm({
      title: t('Disable two-factor authentication'),
      message: t('Are you sure? Your account will only be protected by your password.'),
      confirmLabel: t('Disable'), danger: true,
    }))) return;
    setError(''); setBusy(true);
    try {
      await api.post('/auth/2fa/disable', { password });
      setEnabled(false);
      setPassword('');
      toast(t('Two-factor authentication disabled'));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  if (enabled === null) return null;

  return (
    <section className="panel" style={{ maxWidth: 460 }}>
      <h2 className="panel-title">{t('Two-factor authentication')}</h2>
      {error && <div className="alert error">{error}</div>}

      {recoveryCodes ? (
        <>
          <div className="alert success">{t('Save these recovery codes somewhere safe — each one can be used once if you lose access to your authenticator app. They will not be shown again.')}</div>
          <ul className="line-list" style={{ fontFamily: 'monospace', fontSize: 14 }}>
            {recoveryCodes.map((c) => <li key={c}>{c}</li>)}
          </ul>
          <button className="btn primary sm" onClick={() => setRecoveryCodes(null)}>{t('Done')}</button>
        </>
      ) : enabled ? (
        <form onSubmit={(e) => { e.preventDefault(); disable(); }}>
          <p className="muted sm">{t('Two-factor authentication is enabled on your account.')}</p>
          {/* Hidden username so password managers associate the credential correctly. */}
          <input type="text" name="username" autoComplete="username" value={user?.email || ''} readOnly hidden />
          <Field label={t('Current password')}>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          </Field>
          <button type="submit" className="btn danger ghost sm" disabled={busy || !password}>
            {busy ? t('Saving…') : t('Disable two-factor authentication')}
          </button>
        </form>
      ) : enrolling ? (
        <>
          <p className="muted sm">{t('Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password…), then enter the 6-digit code it shows.')}</p>
          {qrDataUrl && <img src={qrDataUrl} alt="TOTP QR code" width={220} height={220} style={{ display: 'block', margin: '8px 0' }} />}
          <p className="muted sm">{t('Or enter this code manually:')} <code>{secret}</code></p>
          <Field label={t('6-digit code')}>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" autoFocus />
          </Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn ghost sm" onClick={() => setEnrolling(false)}>{t('Cancel')}</button>
            <button className="btn primary sm" onClick={confirmEnroll} disabled={busy || code.trim().length < 6}>
              {busy ? t('Verifying…') : t('Confirm and enable')}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="muted sm">{t('Add an extra layer of security: after your password, you will also need a 6-digit code from an authenticator app.')}</p>
          <button className="btn primary sm" onClick={startEnroll} disabled={busy}>{t('Enable two-factor authentication')}</button>
        </>
      )}
    </section>
  );
}
